import { Card, CardHeader, CardBody } from '@heroui/react'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import type { AnalyticsSummary } from '../../types'

interface FunnelPerformanceChartProps {
  data: AnalyticsSummary
}

/**
 * Renders a combined bar + line chart comparing funnel impressions, conversions,
 * and conversion rate. Colors are driven by `--fn-chart-*` CSS variables
 * (defined in funnels-variables.css) so they adapt to light/dark mode and
 * can be overridden per-theme without touching this component.
 */
export function FunnelPerformanceChart({ data }: FunnelPerformanceChartProps) {
  const chartData = data.topFunnels.map((funnel: { id: string; name: string; impressions: number; conversions: number }) => ({
    name: funnel.name || `Funnel ${funnel.id.slice(0, 8)}`,
    impressions: funnel.impressions,
    conversions: funnel.conversions,
    conversionRate:
      funnel.impressions > 0
        ? Number(((funnel.conversions / funnel.impressions) * 100).toFixed(2))
        : 0,
  }))

  const hasData = chartData.length > 0 && chartData.some((d: { impressions: number }) => d.impressions > 0)

  if (!hasData) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Funnel Performance</h3>
        </CardHeader>
        <CardBody>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-tf-text-muted mb-2">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <p className="text-tf-text-muted font-medium">No performance data yet</p>
            <p className="text-sm text-tf-text-muted mt-1">
              Metrics will appear once funnels receive traffic
            </p>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold">Funnel Performance</h3>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--tf-border-subdued)" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: 'var(--tf-text-secondary)' }}
              angle={-15}
              textAnchor="end"
              height={80}
              stroke="var(--tf-border)"
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12, fill: 'var(--tf-text-secondary)' }}
              stroke="var(--tf-border)"
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12, fill: 'var(--tf-text-secondary)' }}
              stroke="var(--tf-border)"
              domain={[0, 100]}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--tf-bg-card)',
                border: '1px solid var(--tf-border)',
                borderRadius: '8px',
                padding: '12px',
                color: 'var(--tf-text-primary)',
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12, color: 'var(--tf-text-secondary)' }} />
            <Bar
              yAxisId="left"
              dataKey="impressions"
              fill="var(--fn-chart-impressions)"
              name="Impressions"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              yAxisId="left"
              dataKey="conversions"
              fill="var(--fn-chart-conversions)"
              name="Conversions"
              radius={[4, 4, 0, 0]}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="conversionRate"
              stroke="var(--fn-chart-rate)"
              strokeWidth={2}
              name="Conversion Rate (%)"
              dot={{ r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}
